import React, { useMemo } from "react";
import { Button } from "@mui/material";
import DownloadIcon from "@mui/icons-material/Download";
import jsPDF from "jspdf";
import html2canvas from "html2canvas";
import {
  formatContentForPDF,
  addPDFHeader,
  hasArabicCharacters,
} from "../../../utils/pdfUtils";
import { safeJSONParse } from "../../../utils/helper";
import styles from "../agent-response.module.scss";
import finalImg1 from "../../../assets/images/ic-generatedassets.svg";
import finalImg3 from "../../../assets/images/Note.svg";
import finalImg4 from "../../../assets/images/TimerIcon.svg";
import finalImg5 from "../../../assets/images/Brain.svg";
import { Section } from "../helper";

type WorkflowEndAgentProps = {
  parsedContent: any;
  currentChat: any[];
};

// Section configuration mapping for dynamic rendering
const SECTION_CONFIG = {
  under_the_hood: {
    title: "Under the Hood",
    img: finalImg3,
  },
  processing_time: {
    title: "Processing Time",
    img: finalImg4,
  },
  intelligence_codified: {
    title: "Intelligence Codified",
    img: finalImg5,
  },
} as const;

export default function WorkflowEndAgent({
  parsedContent,
  currentChat,
}: WorkflowEndAgentProps): JSX.Element {
  // Extract finalAnswer from parsedContent
  const finalAnswer = useMemo(
    () => safeJSONParse(parsedContent?.finalAnswer),
    [parsedContent]
  );

  // Build sections dynamically from finalAnswer
  const sectionList: Section[] = useMemo(() => {
    const sections: Section[] = [];

    // First section: Generated Assets (static or from finalAnswer)
    const generatedAssets = finalAnswer?.generated_assets || finalAnswer?.payload?.generated_assets;
    if (generatedAssets) {
      sections.push({
        title: "Generated Assets",
        text: (
          <div className={styles.generatedAssets}>
            {Array.isArray(generatedAssets) ? (
              <ul>
                {generatedAssets.map((asset: string, idx: number) => (
                  <li key={idx}>{asset}</li>
                ))}
              </ul>
            ) : typeof generatedAssets === "string" ? (
              <p>{generatedAssets}</p>
            ) : null}
          </div>
        ),
        img: finalImg1,
      });
    } else {
      // Default static generated assets if not provided
      sections.push({
        title: "Generated Assets",
        text: (
          <div className={styles.generatedAssets}>
            <ul>
              <li>Press Release (English)</li>
              <li>Press Release (Arabic)</li>
              <li>Media Briefing Q&A</li>
              <li>X Post</li>
            </ul>
            <ul>
              <li>Instagram Post</li>
              <li>LinkedIn Post</li>
              <li>Internal Communication Email</li>
            </ul>
          </div>
        ),
        img: finalImg1,
      });
    }

    // Dynamically add sections from finalAnswer based on SECTION_CONFIG
    Object.entries(SECTION_CONFIG).forEach(([key, config]) => {
      const value = finalAnswer?.[key] || finalAnswer?.payload?.[key];
      if (value) {
        sections.push({
          title: config.title,
          text: typeof value === "string" ? value : JSON.stringify(value),
          img: config.img,
        });
      }
    });

    return sections;
  }, [finalAnswer]);

  // Handler for downloading all workflow content
  const handleDownloadAllContent = async () => {
    try {
      const targetAgents = [
        "press_release_agent",
        "localizer_agent",
        "media_brief_agent",
        "social_media_insights_agent",
        "internal_communication_agent",
      ];

      // Filter agent messages from chat history
      const agentMessages = currentChat.filter(
        (msg: any) =>
          msg.sender === "agent" &&
          msg.agentIdName &&
          targetAgents.includes(msg.agentIdName) &&
          msg.message && // Ensure message exists
          msg.message.trim() !== "" // Ensure message is not empty
      );

      if (agentMessages.length === 0) {
        console.warn("No agent content found to download");
        alert("No workflow content available to download.");
        return;
      }

      console.log(
        `Found ${agentMessages.length} agent messages to include in PDF`
      );

      const pdf = new jsPDF();
      const pageWidth = pdf.internal.pageSize.getWidth();
      const pageHeight = pdf.internal.pageSize.getHeight();
      const margin = 20;

      // Add main header only on first page
      addPDFHeader(pdf, "Complete Workflow Report", margin, pageWidth);

      // Helper function to render Arabic content using canvas (batched approach)
      const renderArabicContent = async (
        formattedText: string,
        startY: number
      ): Promise<void> => {
        const paragraphs = formattedText
          .split("\n")
          .filter((line) => line.trim());

        if (paragraphs.length === 0) {
          console.warn("No paragraphs to render");
          return;
        }

        let currentY = startY;
        const bottomPadding = 20;

        // Render paragraphs in batches
        const renderBatch = async (
          batch: string[],
          yPosition: number
        ): Promise<number> => {
          return new Promise((resolve, reject) => {
            const tempDiv = document.createElement("div");
            tempDiv.style.position = "absolute";
            tempDiv.style.left = "-9999px";
            tempDiv.style.fontFamily = "Arial, Tahoma, sans-serif";
            tempDiv.style.fontSize = "14px";
            tempDiv.style.lineHeight = "1.6";
            tempDiv.style.width = "600px";
            tempDiv.style.padding = "20px";
            tempDiv.style.background = "#ffffff";
            tempDiv.style.color = "#000000";

            tempDiv.innerHTML = batch
              .map((p) => {
                const isArabic = hasArabicCharacters(p);
                const direction = isArabic ? "rtl" : "ltr";
                const textAlign = isArabic ? "right" : "left";
                return `<p style="margin: 0 0 15px 0; direction: ${direction}; text-align: ${textAlign};">${p}</p>`;
              })
              .join("");

            document.body.appendChild(tempDiv);

            html2canvas(tempDiv, {
              scale: 2,
              useCORS: true,
              logging: false,
              backgroundColor: "#ffffff",
            })
              .then((canvas) => {
                const imgWidth = pageWidth - 2 * margin;
                const imgHeight = (canvas.height * imgWidth) / canvas.width;
                const imgData = canvas.toDataURL("image/png");

                pdf.addImage(
                  imgData,
                  "PNG",
                  margin,
                  yPosition,
                  imgWidth,
                  imgHeight
                );
                document.body.removeChild(tempDiv);
                resolve(imgHeight);
              })
              .catch((error) => {
                document.body.removeChild(tempDiv);
                reject(error);
              });
          });
        };

        // Process paragraphs with batching logic
        let currentBatch: string[] = [];

        for (let i = 0; i < paragraphs.length; i++) {
          const testBatch = [...currentBatch, paragraphs[i]];

          // Test if batch fits on current page
          const tempDiv = document.createElement("div");
          tempDiv.style.position = "absolute";
          tempDiv.style.left = "-9999px";
          tempDiv.style.fontFamily = "Arial, Tahoma, sans-serif";
          tempDiv.style.fontSize = "14px";
          tempDiv.style.lineHeight = "1.6";
          tempDiv.style.width = "600px";
          tempDiv.style.padding = "20px";

          tempDiv.innerHTML = testBatch
            .map((p) => {
              const isArabic = hasArabicCharacters(p);
              const direction = isArabic ? "rtl" : "ltr";
              const textAlign = isArabic ? "right" : "left";
              return `<p style="margin: 0 0 15px 0; direction: ${direction}; text-align: ${textAlign};">${p}</p>`;
            })
            .join("");

          document.body.appendChild(tempDiv);
          const testHeight = tempDiv.offsetHeight;
          document.body.removeChild(tempDiv);

          const imgWidth = pageWidth - 2 * margin;
          const estimatedPdfHeight = (testHeight * imgWidth) / 600;
          const availableHeight = pageHeight - currentY - bottomPadding;

          if (estimatedPdfHeight > availableHeight && currentBatch.length > 0) {
            // Render current batch
            try {
              await renderBatch(currentBatch, currentY);
            } catch (error) {
              console.error("Error rendering batch:", error);
            }

            // Start new page
            pdf.addPage();
            currentY = margin + 10;
            currentBatch = [paragraphs[i]];
          } else {
            currentBatch = testBatch;
          }
        }

        // Render remaining batch
        if (currentBatch.length > 0) {
          try {
            await renderBatch(currentBatch, currentY);
          } catch (error) {
            console.error("Error rendering final batch:", error);
          }
        }
      };

      // Helper function to render English text content
      const renderTextContent = (
        formattedText: string,
        startY: number
      ): void => {
        const lineHeight = 7;
        const maxWidth = pageWidth - 2 * margin;

        pdf.setFontSize(11);
        pdf.setFont("helvetica", "normal");
        const lines = pdf.splitTextToSize(formattedText, maxWidth);
        let y = startY;

        lines.forEach((line: string) => {
          if (y + lineHeight > pageHeight - margin - 10) {
            pdf.addPage();
            y = margin + 10;
          }
          pdf.text(line, margin, y);
          y += lineHeight;
        });
      };

      // Process each agent's content
      for (let i = 0; i < agentMessages.length; i++) {
        const msg = agentMessages[i];
        const isFirst = i === 0;

        try {
          // Add new page for each agent except the first
          if (!isFirst) {
            pdf.addPage();
          }

          const agentName = msg.agentIdName
            .replace(/_/g, " ")
            .replace(/\b\w/g, (l: string) => l.toUpperCase());

          console.log(`Processing ${agentName}...`);

          // Calculate header position
          const headerY = isFirst ? margin + 25 : margin;

          // Add agent section header
          pdf.setFontSize(14);
          pdf.setFont("helvetica", "bold");
          pdf.text(agentName, margin, headerY);

          pdf.setDrawColor(200, 200, 200);
          pdf.line(margin, headerY + 5, pageWidth - margin, headerY + 5);

          // Format content for this agent
          const formattedText = formatContentForPDF(
            msg.message,
            msg.agentIdName
          );

          if (!formattedText || formattedText.trim() === "") {
            console.warn(`No formatted content for ${agentName}`);
            pdf.setFontSize(11);
            pdf.setFont("helvetica", "italic");
            pdf.text("No content available", margin, headerY + 20);
            continue;
          }

          const containsArabic = hasArabicCharacters(formattedText);

          // Determine if we need canvas rendering for Arabic
          const isLocalizerAgent =
            msg.agentIdName === "LocalizerAgent" ||
            msg.agentIdName === "localizer_agent";
          const isSocialMediaAgent =
            msg.agentIdName === "social_media_insights_agent";

          const needsArabicRendering =
            isLocalizerAgent || (isSocialMediaAgent && containsArabic);

          // Content starts below the header
          const contentStartY = headerY + 15;

          // Render content based on language
          if (needsArabicRendering) {
            await renderArabicContent(formattedText, contentStartY);
          } else {
            renderTextContent(formattedText, contentStartY);
          }

          console.log(`✓ ${agentName} processed successfully`);
        } catch (error) {
          console.error(`Error processing ${msg.agentIdName}:`, error);
          // Continue with next agent instead of failing completely
        }
      }

      // Save the combined PDF
      const fileName = `workflow_complete_${new Date().toISOString().slice(0, 10)}_${Date.now()}.pdf`;
      pdf.save(fileName);
      console.log(`✓ PDF generated successfully: ${fileName}`);
    } catch (error) {
      console.error("Error downloading workflow content:", error);
      alert(
        "Failed to download workflow content. Please check the console for details and try again."
      );
    }
  };

  return (
    <div className={styles.agentsinvoked}>
      <div className={styles.outerBox}>
        <div className={styles.sectionsGrid}>
          {sectionList.map((sec: Section, index: number) => (
            <div className={styles.sectionCard} key={index}>
              <div className={styles.titleRow}>
                <img src={sec.img} alt="icon" className={styles.secIcon} />
                <h3 className={styles.sectionTitle}>{sec.title}</h3>
              </div>
              <div className={styles.sectionText}>{sec.text}</div>
            </div>
          ))}
        </div>

        {/* Download All Content Button */}
        <div style={{ marginTop: "24px", textAlign: "center" }}>
          <Button
            variant="contained"
            color="primary"
            startIcon={<DownloadIcon />}
            onClick={handleDownloadAllContent}
          >
            Download Complete Workflow
          </Button>
        </div>
      </div>
    </div>
  );
}
